import { createContext, useContext, useState, useEffect, useCallback, useRef} from "react";
import { questions as allQuestions } from "../Data/Questions";
import { useNavigate } from "react-router-dom";

const GameContext = createContext();
export const useGame = () => useContext(GameContext);

export function GameProvider({ children, settings }) {
    const navigate = useNavigate();

    // Extract settings passed from HomePage
    const { username, difficulty, modifier, leaderboardKey } = settings;

    // Game States
    const [gameQuestions, setGameQuestions] = useState([]); // Array of 10 random questions
    const [index, setIndex] = useState(0);                  // Current question index
    const [input, setInput] = useState("");                 // User input typing
    const [score, setScore] = useState(0);                  // Score increase my remaining time
    const [feedback, setFeedback] = useState("");           // Result feedback "Correct" or "Wrong"
    const [inFeedback, setInFeedback] = useState(false);    // Boolean to prevent double submission
    const [showPrompt, setShowPrompt] = useState(true);     // Boolean for hidden mode
    const [timeLeft, setTimeLeft] = useState(10);           // Time per question
    const [startTime, setStartTime] = useState(Date.now()); // For total time spent
    const [isLoading, setIsLoading] = useState(true);

    const hasTriggeredRef = useRef(false);
    const gameEndedRef = useRef(false);
    const timeLeftRef = useRef(10); // Use ref to track actual time for scoring
    
    // Custom gameQuestions (Author: TANG Huyming)
    const extractCustomQuestions = () => {
        const customQuestionsString = localStorage.getItem("customQuestions");
        if(!customQuestionsString || customQuestionsString.length === 0) {
            return [];
        }
        const parsed = JSON.parse(customQuestionsString);
        if(Array.isArray(parsed) && parsed !== undefined) {
            return parsed;
        }
        return [];
    }
    const [customQuestions] = useState(extractCustomQuestions);

    // Duration before hiding prompt (for hidden mode)
    const revealTimes = { easy: 6, medium: 4, hard: 2 };    
    const revealTime = revealTimes[difficulty];

    // Start Game: 
    // Pick 10 random question based on the difficulty
    const startGame = useCallback(async () => {
        setIsLoading(true);
        gameEndedRef.current = false; // Reset game ended flag
        const combinedQuestions = [...allQuestions, ...customQuestions];
        const filtered = combinedQuestions.filter(q => q.difficulty === difficulty);
        const shuffled = filtered.sort(() => Math.random() - 0.5);
        const selectedTen = shuffled.slice(0, 10);

        // Reset game states
        setGameQuestions(selectedTen);
        setIsLoading(false);
        setIndex(0);
        setScore(0);
        setInput("");
        setStartTime(Date.now());
    }, [customQuestions, difficulty]);

    // Ending the game and store result in localStorage
    const endGame = useCallback((latestScore) => {
        // Prevent duplicate saves
        if (gameEndedRef.current) return;
        gameEndedRef.current = true;

        const timeTaken = ((Date.now() - startTime) / 1000).toFixed(1);
        const key = leaderboardKey || "defaultLeaderboard";

        // Get previous scores
        const existing = JSON.parse(localStorage.getItem(key)) || [];
        
        // Add the new entry in 
        const newEntry = { username, difficulty, modifier, score: latestScore, timeTaken };
        localStorage.setItem(key, JSON.stringify([...existing, newEntry]));

        // Go to leaderboard
        navigate("/scoreboard", {
            replace: true,
        });
    }, [leaderboardKey, navigate, username, difficulty, modifier, startTime]);

    // Process answer result
    const handleResult = useCallback((correct) => {
        // Prevent double triggering
        if (inFeedback || gameEndedRef.current) return;

        setInFeedback(true);
        setFeedback(correct ? "✔ Correct!" : "✘ Wrong!");
        
        // Update score based on remaining time (use ref for accurate value)
        if (correct) {
            const base = timeLeftRef.current;
            const bonus = modifier === "sonicSpeed" ? base * 2 : base;
            setScore(prev => prev + bonus);
        }

        // Move to next question or Endgame
        setTimeout(() => {
            setInFeedback(false);
            if (index < gameQuestions.length - 1) {
                setIndex(prev => prev + 1);
            } else {
                // Ensure that the endGame() method receives the latest score
                setScore(latest => {
                    endGame(latest); // send the latest score to endGame
                    return latest; // update score with the same value
                })
            }
        }, 1000);
    }, [gameQuestions.length, inFeedback, index, modifier, endGame]);

    // Handle typing and update input state
    const handleTyping = (val) => {
        setInput(val);

        const question = gameQuestions[index];
        if (!question) return;

        // In perfect mode, any wrong letter, instantly fail
        if (modifier === "perfect" && val.length > 0) {
            const allowed = question.answers.some(ans =>
                ans.toLowerCase().startsWith(val.toLowerCase())
            );
            if (!allowed) handleResult(false);
        }
    };

    // Sync timeLeft state with ref
    useEffect(() => {
        timeLeftRef.current = timeLeft;
    }, [timeLeft]);

    // Timer logic (default 10s, and 5s for sonicSpeed mode)
    useEffect(() => {
        if (!gameQuestions.length || isLoading) return;

        const baseTime = modifier === "sonicSpeed" ? 5 : 10;
        setTimeLeft(baseTime);
        timeLeftRef.current = baseTime;
        hasTriggeredRef.current = false;

        const interval = setInterval(() => {
            setTimeLeft(prev => {
                const newTime = prev - 1;
                timeLeftRef.current = newTime; // Keep ref in sync
                
                if (newTime <= 0) {
                    clearInterval(interval);
                    
                    if (!hasTriggeredRef.current && !gameEndedRef.current) {
                        hasTriggeredRef.current = true;
                        setInFeedback(currentFeedback => {
                            if (!currentFeedback) {
                                setTimeout(() => handleResult(false), 0);
                            }
                            return currentFeedback;
                        });
                    }
                    return 0;
                }
                return newTime;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [index, gameQuestions, isLoading, handleResult, modifier]); // Removed 'score' from dependencies

    // Hidden mode: After x seconds, hide the question (prompt)
    useEffect(() => {
        // Reset the prompt to visible
        setShowPrompt(true);
    
        // Only apply hidden mode reveal delay
        if (modifier === "hidden") {
            const timer = setTimeout(() => {
                setShowPrompt(false);
            }, revealTime * 1000);
    
            return () => clearTimeout(timer);
        }
    }, [index, modifier, revealTime]);
    
    // Reset input & showPrompt on new question
    useEffect(() => {
        setInput("");
        setShowPrompt(true);
    }, [index]);

    // Provide all game values and functions to components
    return (
        <GameContext.Provider value={{
            gameQuestions,
            index,
            input,
            feedback,
            inFeedback,
            isLoading,
            showPrompt,
            timeLeft,
            score,
            startGame,
            handleTyping,
            handleResult,
            setInput
        }}>
            {children}
        </GameContext.Provider>
    );
}